/**
 * Multiplayer orchestrator — drives a shared-round Live Ball session via the
 * SignalR hub. Mirrors the event handling in
 *   packages/amgdragontiger_v2/lib/presentation/bloc/game_bloc.dart (MP path).
 *
 * Lifecycle:
 *   start()
 *     ├─ login / use URL params
 *     ├─ openGame with the M-suffixed gameKey
 *     ├─ /action/balance + /action/getinfo (REQUIRED priming — without these
 *     │   the hub rejects JoinGame with "Invalid Player")
 *     ├─ connect to <hubUrl> (SignalR)
 *     ├─ joinGame(sessionId, gameKey)
 *     └─ getMessages — replay any in-flight round events
 *
 * Each hub event arrives as a clean `MpEnvelope` from the service. Envelopes
 * carry an `Index` for de-dup, a `Flag` ("Success"|"Fail") for error
 * propagation, and event-specific fields (`StartTime`/`EndTime`, `Result`,
 * `Data`). For the `Result` event, `Data` is an **array of per-user lines**
 * with `UserName` + `WinLost` + `Balance`; the orchestrator filters by the
 * current player's username to pull out their numbers.
 *
 * BATAL / PASANG are hidden in MP. ULANG replays last round's bets by
 * re-invoking hub `PlaceBet` for each saved entry.
 */
import {
  BET_COUNTDOWN_OFFSET_SEC,
  DEFAULT_AGENT_ID,
  DEFAULT_PASSWORD,
  HUB_URLS,
  RESULT_HOLD_MS,
} from '../config';
import type { Api } from '../net/api';
import type { LotteryMachine } from '../lib/lottery-machine';
import { MultiplayerService } from '../net/multiplayerService';
import type { MpEnvelope, MultiplayerEvent } from '../net/multiplayerService';
import type { BetPanel } from '../ui/betPanel';
import { appendDebugLog } from '../ui/debugPanel';
import type { Topbar } from '../ui/topbar';
import { displayedBalance } from './gameState';
import type { GameState, GameStateSnapshot } from './gameState';
import type { UrlParams } from './urlParams';
import { getBet } from './variants';
import { debugLog, error as logError, warn } from '../util/logger';

interface OrchestratorDeps {
  state: GameState;
  api: Api;
  machine: LotteryMachine;
  panel: BetPanel;
  topbar: Topbar;
  params: UrlParams;
}

type MpPhase = 'connecting' | 'idle' | 'betting' | 'noMoreBet' | 'playing' | 'showResult';

export class MultiplayerOrchestrator {
  private readonly d: OrchestratorDeps;
  /**
   * URL-DECODED player session token. This is what JoinGame's first arg
   * expects — NOT the gsid UUID. Probed directly against staging:
   * passing the gsid yields "Invalid Player"; the long alphanumeric session
   * token from /action/login (or the URL-decoded `?token=` lobby hands us)
   * is the right value.
   */
  private playerSessionToken = '';
  private mp = new MultiplayerService();
  private currentBallNumber = 0;
  private winningBetIds: number[] = [];
  private currentWinLose = 0;
  private currentAfterBalance: number | null = null;
  /** Set true once the ball-land animation finishes for the current round. */
  private animationDone = false;
  /** Set true after the player presses PASANG and bets are sent to server. */
  private submitted = false;
  /** Set true once the orchestrator is being torn down — suppresses the
   * disconnect dialog so it doesn't fire on legitimate unmount. */
  private disposed = false;
  private mpPhase: MpPhase = 'connecting';

  // Server's Index counter — drop any event whose Index has already been
  // processed (replay deduplication).
  private lastIndex = -1;

  // Local 1 Hz countdown (server authoritative — we just render).
  private tickTimer: number | null = null;
  private tickRemaining = 0;

  constructor(deps: OrchestratorDeps) {
    this.d = deps;
  }

  // ============================================================
  // Public surface (matches GameOrchestrator)
  // ============================================================

  async start(): Promise<void> {
    this.d.state.on((next, prev) => this.onStateChange(next, prev));
    this.d.state.setPhase('initializing');
    this.d.topbar.setStatus('Connecting…');

    try {
      await this.signIn();
    } catch (e) {
      logError('[MP] sign-in failed', e);
      this.d.state.setError(`Could not start session: ${(e as Error).message}`);
      this.d.topbar.setStatus('Sign-in failed');
      return;
    }

    try {
      await this.connectHub();
    } catch (e) {
      logError('[MP] hub connect failed', e);
      this.d.state.setError(
        'Multiplayer hub unavailable. Backend may not be deployed yet.',
      );
      this.d.topbar.setStatus('Hub offline');
      this.d.panel.setBettingEnabled(false);
      return;
    }
  }

  tryPlaceBet(betKey: string): void {
    if (this.mpPhase !== 'betting') return;
    if (this.submitted) {
      // Already PASANG'd this round; new chips would need another PASANG cycle
      // but we lock the panel after submit, so this is just defensive.
      return;
    }
    const chip = this.d.state.selectedChipValue;
    const ok = this.d.state.placeBet(betKey, chip);
    if (!ok) {
      this.d.topbar.setStatus('Bet rejected — check balance / limit');
    }
  }

  /**
   * BATAL — clear staged (un-PASANG'd) bets. After PASANG the bets are with
   * the server and cannot be cancelled; this method is a no-op in that case.
   */
  clearAll(): void {
    if (this.mpPhase !== 'betting') return;
    if (this.submitted) {
      this.d.topbar.setStatus('Bets already placed — cannot cancel');
      return;
    }
    this.d.state.clearBets();
    this.d.panel.clearAllStakes();
  }

  /**
   * ULANG — repeat last round's bets locally (does NOT send to server yet).
   * Player still needs to press PASANG to commit.
   */
  repeatLast(): void {
    if (this.mpPhase !== 'betting') return;
    if (this.submitted) {
      this.d.topbar.setStatus('Bets already placed — cannot repeat');
      return;
    }
    const ok = this.d.state.repeatBets();
    if (!ok) {
      this.d.topbar.setStatus('Nothing to repeat');
      return;
    }
    const bets = this.d.state.bets;
    for (const k of bets.keys) this.d.panel.setStake(k, bets.amountFor(k));
  }

  /**
   * PASANG — submit all staged bets to the server in one batch. The server's
   * `PlaceBet` invoke is per-chip, so we send N invocations in sequence and
   * lock the panel until the next round.
   */
  async confirm(): Promise<void> {
    if (this.mpPhase !== 'betting') return;
    if (this.submitted) return;
    const bets = this.d.state.bets;
    if (bets.isEmpty) {
      this.d.topbar.setStatus('Pick a number first');
      return;
    }
    this.submitted = true;
    this.d.panel.setBettingEnabled(false);
    this.d.state.storeAsLastConfirmed();
    const variant = this.d.state.variant;
    let sent = 0;
    for (const [key, amount] of bets.asMap()) {
      const def = getBet(variant, key);
      if (!def) continue;
      try {
        await this.mp.placeBet(def.id, amount);
        sent++;
      } catch (e) {
        warn('[MP] PlaceBet failed', key, amount, e);
      }
    }
    this.d.topbar.setStatus(`Placed ${sent} bet${sent === 1 ? '' : 's'}`);
  }

  selectChip(i: number): void {
    this.d.state.selectChip(i);
  }

  dispose(): void {
    this.disposed = true;
    this.stopTick();
    void this.mp.disconnect();
  }

  // ============================================================
  // Bootstrap
  // ============================================================

  private async signIn(): Promise<void> {
    const { params } = this.d;
    const agentId = params.agentId || DEFAULT_AGENT_ID;
    const apiGameKey = params.apiGameKey;

    let bearerToken: string;
    let gsid: string;

    if (params.token && params.gsid) {
      debugLog('[MP] using token+gsid from URL');
      // URLSearchParams already decoded the token. Keep the raw form for
      // JoinGame; re-encode separately for the HTTP Authorization header.
      this.playerSessionToken = params.token;
      bearerToken = ensureUrlEncoded(params.token);
      gsid = params.gsid;
    } else {
      this.d.topbar.setStatus('Logging in…');
      const login = await this.d.api.login({
        userName: params.userName,
        agentId,
        password: DEFAULT_PASSWORD,
      });
      if (!login.sessionId) throw new Error('Login response missing SessionId');
      this.d.api.setCredentials({
        sessionToken: login.sessionId,
        gameSessionId: null,
        agentId: login.agentId || agentId,
        userName: params.userName,
      });

      this.d.topbar.setStatus('Opening game…');
      const session = await this.d.api.openGame({
        userName: params.userName,
        agentId: login.agentId || agentId,
        gameKey: apiGameKey,
      });
      const parsed = parseGameUrl(session.currentGameUrl, login.sessionId);
      // The raw `gameToken` from the URL query string is decoded by parseGameUrl
      // (URL.searchParams.get auto-decodes). That's the form JoinGame wants.
      this.playerSessionToken = parsed.gameToken;
      bearerToken = ensureUrlEncoded(parsed.gameToken);
      gsid = parsed.gsid || session.sessionId;
      this.d.state.setBalance(session.balance);
      this.applyServerLimits(session.chips, session.minBet, session.maxBet);
    }

    this.d.api.setCredentials({
      sessionToken: bearerToken,
      gameSessionId: gsid,
      agentId,
      userName: params.userName,
    });

    // /action/balance + /action/getinfo are defensive priming. Confirmed by
    // probe: JoinGame works whether or not these succeed (they sometimes
    // return ErrorCode 99 for M-suffixed keys and the hub still joins).
    // Keeping them aligned with the dragontiger reference SP path.
    this.d.topbar.setStatus('Fetching balance…');
    try {
      const bal = await this.d.api.checkBalance({ sessionId: gsid, gameKey: apiGameKey });
      this.d.state.setBalance(bal);
    } catch (e) {
      warn('[MP] checkBalance failed (continuing):', e);
    }
    try {
      const info = await this.d.api.getInfo({ sessionId: gsid, gameKey: apiGameKey });
      if (info.balance > 0) this.d.state.setBalance(info.balance);
      this.applyServerLimits(info.chips, info.minBet, info.maxBet);
    } catch (e) {
      warn('[MP] getInfo failed (continuing):', e);
    }
  }

  private applyServerLimits(chips: number[], minBet: number, maxBet: number): void {
    const cur = this.d.state.config;
    const next = {
      chipValues: chips.length > 0 ? chips : [...cur.chipValues],
      minBet: minBet > 0 ? minBet : cur.minBet,
      maxBet: maxBet > 0 ? maxBet : cur.maxBet,
    };
    this.d.state.setConfig(next);
  }

  // ============================================================
  // Hub wiring
  // ============================================================

  private async connectHub(): Promise<void> {
    const url = HUB_URLS[this.d.state.variant.key];
    this.d.topbar.setStatus('Joining room…');
    this.mp.on((evt) => this.onHubEvent(evt));
    await this.mp.connect(url);
    // CRITICAL: JoinGame's first arg is the PLAYER SESSION TOKEN (URL-decoded),
    // not the gsid UUID. Confirmed by direct wire probe against staging and
    // by the dragontiger_v2 reference (game_screen.dart's _startMultiplayer
    // decodes the token via `Uri.decodeComponent` before dispatching) and by
    // carracing_v2 (which names the service parameter `token` honestly).
    // Passing the gsid yields "Failed to Join Game. Invalid Player."
    await this.mp.joinGame(this.playerSessionToken, this.d.params.apiGameKey);
    await this.mp.getMessages();
    this.d.topbar.setStatus('Waiting for next round…');
    this.mpPhase = 'idle';
  }

  private onHubEvent(evt: MultiplayerEvent): void {
    if (evt.name === '_connection') {
      this.handleConnectionStateChange(evt.raw as Record<string, unknown>);
      return;
    }

    const env = evt.envelope;
    if (!env) return;

    // Index dedup — drop replays we've already processed.
    if (typeof env.Index === 'number') {
      if (env.Index <= this.lastIndex) {
        debugLog('[MP] dropping replay', evt.name, env.Index);
        return;
      }
      this.lastIndex = env.Index;
    }

    // Server-side failure → surface as status; don't drive phase changes from it.
    if (env.Flag === 'Fail') {
      const msg = env.Content || `${evt.name} failed`;
      logError('[MP] server failure', evt.name, msg);
      this.d.topbar.setStatus(`Server error: ${msg}`);
      if (evt.name === 'PlayerJoined') {
        // Couldn't join (e.g. session expired) — disable betting.
        this.d.panel.setBettingEnabled(false);
        this.d.state.setError(msg);
      }
      return;
    }

    debugLog('[MP] event', evt.name, env);
    switch (evt.name) {
      case 'PlayerJoined':
        this.handlePlayerJoined(env);
        break;
      case 'PlayerLeft':
        this.handlePlayerLeft(env);
        break;
      case 'StartGame':
        this.handleStartGame();
        break;
      case 'StartBet':
        this.handleStartBet(env);
        break;
      case 'EndBet':
        this.handleEndBet();
        break;
      case 'StartPlay':
        void this.handleStartPlay(env);
        break;
      case 'Result':
        this.handleResult(env);
        break;
      case 'PlaceBet':
        this.handlePlaceBetBroadcast(env);
        break;
      case 'GetMessages':
        // Replay items arrive through the same registered events; nothing
        // additional to do here.
        break;
    }
  }

  private handleConnectionStateChange(p: Record<string, unknown> | null): void {
    if (!p) return;
    const state = p.state;
    if (state === 'reconnecting') this.d.topbar.setStatus('Reconnecting…');
    else if (state === 'reconnected' || state === 'connected') this.d.topbar.setStatus('Connected');
    else if (state === 'closed') {
      this.d.topbar.setStatus('Disconnected');
      this.d.panel.setBettingEnabled(false);
      this.showDisconnectDialog('You have been disconnected from the game. Press OK to return to the lobby.');
    }
  }

  /**
   * Show the modal disconnect dialog. On OK, redirect to the lobby URL —
   * preferring the `exitUrl` query param passed by the lobby launcher, then
   * the document referrer if it's a different origin, falling back to the
   * known amglobby production domain.
   */
  private showDisconnectDialog(message: string): void {
    if (this.disposed) return;
    if (typeof document === 'undefined') return;
    const overlay = document.getElementById('disconnect-overlay');
    const msgEl = document.getElementById('disconnect-message');
    const okBtn = document.getElementById('disconnect-ok');
    if (!overlay || !okBtn) return;
    if (overlay.classList.contains('visible')) return; // already showing
    if (msgEl) msgEl.textContent = message;
    overlay.classList.add('visible');
    const onClick = () => {
      okBtn.removeEventListener('click', onClick);
      overlay.classList.remove('visible');
      const target = this.resolveLobbyUrl();
      window.location.href = target;
    };
    okBtn.addEventListener('click', onClick);
  }

  private resolveLobbyUrl(): string {
    const fromParams = this.d.params.exitUrl;
    if (fromParams) return fromParams;
    try {
      if (document.referrer && new URL(document.referrer).origin !== window.location.origin) {
        return document.referrer;
      }
    } catch {
      // ignore malformed referrer
    }
    return 'https://amglobby.pages.dev/';
  }

  /**
   * PlayerJoined.Data is a per-player descriptor, with chip limits nested
   * under `Data.Game` (per dragontiger reference). Pull chips/min/max if
   * present so the chip picker re-renders to server values.
   */
  private handlePlayerJoined(env: MpEnvelope): void {
    const dataMap = asMap(env.Data);
    const gameMap = asMap(dataMap?.Game ?? dataMap?.game) ?? dataMap;
    if (!gameMap) return;
    const chipsRaw = gameMap.Chips ?? gameMap.chips;
    const chips = parseChipList(chipsRaw);
    const minBet = num(gameMap.MinBet ?? gameMap.minBet);
    const maxBet = num(gameMap.MaxBet ?? gameMap.maxBet);
    if (chips.length > 0 || minBet > 0 || maxBet > 0) {
      this.applyServerLimits(chips, minBet, maxBet);
    }
  }

  /** Server kicked us — typically inactivity. Stop the room. */
  private handlePlayerLeft(env: MpEnvelope): void {
    const msg = env.Content || 'You have been removed from the game.';
    this.d.topbar.setStatus(msg);
    this.d.panel.setBettingEnabled(false);
    this.showDisconnectDialog(`${msg} Press OK to return to the lobby.`);
    void this.mp.disconnect();
  }

  private handleStartGame(): void {
    this.stopTick();
    this.mpPhase = 'idle';
    this.d.state.resetForNewRound();
    this.d.panel.clearAllStakes();
    this.d.panel.clearHighlights();
    this.d.topbar.hideToast();
    this.d.topbar.hideResultBall();
    this.d.machine.reset();
    this.d.state.setPhase('newRound');
    this.d.topbar.setStatus('New round');
    this.currentBallNumber = 0;
    this.winningBetIds = [];
    this.currentWinLose = 0;
    this.currentAfterBalance = null;
    this.animationDone = false;
    this.submitted = false;
  }

  private handleStartBet(env: MpEnvelope): void {
    const start = env.StartTime ? Date.parse(env.StartTime) : NaN;
    const end = env.EndTime ? Date.parse(env.EndTime) : NaN;
    const totalSec =
      Number.isFinite(start) && Number.isFinite(end)
        ? Math.max(0, Math.round((end - start) / 1000))
        : 30;
    this.startCountdown(totalSec);

    this.mpPhase = 'betting';
    this.d.state.setPhase('betting');
    this.d.panel.setBettingEnabled(true);
    this.d.topbar.setStatus('Place your bets');
  }

  private handleEndBet(): void {
    this.stopTick();
    this.d.topbar.setCountdown(0, 'idle');
    this.mpPhase = 'noMoreBet';
    this.d.state.setPhase('bettingClosed');
    this.d.panel.setBettingEnabled(false);
    if (!this.submitted && !this.d.state.bets.isEmpty) {
      // Player staged chips but never pressed PASANG — stake is forfeit.
      this.d.topbar.setStatus('No more bets — unconfirmed stake discarded');
      this.d.state.clearBets();
      this.d.panel.clearAllStakes();
    } else {
      this.d.topbar.setStatus('No more bets');
    }
  }

  private async handleStartPlay(env: MpEnvelope): Promise<void> {
    const resultStr = String(env.Result ?? '');
    const parsed = parseResultString(resultStr);
    this.currentBallNumber = parsed.ballNumber;
    this.winningBetIds = parsed.winningBetIds;

    this.mpPhase = 'playing';
    this.animationDone = false;
    this.d.state.setPhase('drawing');
    this.d.topbar.setStatus('Drawing ball…');

    try {
      await this.d.machine.playDraw([parsed.ballNumber]);
    } catch (e) {
      warn('[MP] playDraw failed', e);
    }
    this.animationDone = true;
    this.applyPostDrawIfReady();
  }

  /**
   * Result.Data is an ARRAY of per-user entries: `[{UserName, WinLost, Balance, …}]`.
   * Find the entry for the current player and pull their numbers out.
   *
   * `Result` can also fire with `State == 'restart'` carrying a StartTime /
   * EndTime for the round break — we surface that as a status message but
   * don't drive UI from it (no restart-countdown widget yet).
   */
  private handleResult(env: MpEnvelope): void {
    // TEMP debug — surface the raw Result envelope to the on-screen log so we
    // can verify what the server is sending each round. Remove once debugging
    // is complete.
    appendDebugLog('MP Result', env);
    const stateValue = String(env.State ?? '').toLowerCase();
    if (stateValue === 'restart') {
      const start = env.StartTime ? Date.parse(env.StartTime) : NaN;
      const end = env.EndTime ? Date.parse(env.EndTime) : NaN;
      if (Number.isFinite(start) && Number.isFinite(end)) {
        const sec = Math.max(0, Math.round((end - start) / 1000));
        this.d.topbar.setStatus(`Next round in ${sec}s`);
      }
      // Reset dedup so next round's index starts fresh.
      this.lastIndex = -1;
      return;
    }

    const userName = this.d.params.userName;
    const userLine = findUserLine(env.Data, userName);
    if (userLine) {
      const winLose = num(userLine.WinLost ?? userLine.winLost ?? userLine.WinLose ?? userLine.winLose);
      const bal = num(userLine.Balance ?? userLine.balance, -1);
      this.currentWinLose = winLose;
      if (bal >= 0) this.currentAfterBalance = bal;
    }
    // Even without our line we mark the result as "received" so the post-draw
    // gate doesn't hang — fall back to balance unchanged.
    if (this.currentAfterBalance === null) this.currentAfterBalance = this.d.state.balance;
    this.applyPostDrawIfReady();
  }

  /**
   * Server-broadcast PlaceBet confirmation. The server has deducted our stake
   * and would gladly tell us the new balance, but we **deliberately ignore
   * Balance here** so the wallet pill doesn't tick down mid-betting. SP-mode
   * keeps the pre-bet balance visible until the ball lands; MP should match.
   * Balance is applied later from the Result envelope in applyPostDrawIfReady.
   * Local `state.placeBet` already guards against over-betting via
   * `availableBalance = balance - bets.total`.
   *
   * Other players' placements are ignored (no roster UI).
   */
  private handlePlaceBetBroadcast(_env: MpEnvelope): void {
    // intentional no-op — see method docstring
  }

  /**
   * Gate: only commit balance + show toast when ALL of:
   *   - we're in the 'playing' phase (haven't already shown the result)
   *   - the ball-land animation has completed (so the wallet ticks in sync
   *     with the result reveal, matching the SP UX requirement)
   *   - the Result envelope has arrived with a usable balance
   * Either of the two events (animation-complete or Result) may come first.
   */
  private applyPostDrawIfReady(): void {
    if (this.mpPhase !== 'playing') return;
    if (this.currentBallNumber === 0) return;
    if (!this.animationDone) return;
    if (this.currentAfterBalance === null) return;

    if (this.currentAfterBalance >= 0) this.d.state.setBalance(this.currentAfterBalance);

    this.mpPhase = 'showResult';
    this.d.state.setPhase('showResult');
    this.d.panel.highlightWinners(this.currentBallNumber, this.winningBetIds);

    const def = this.d.state.variant.bets.find(
      (b) => b.winsOn.includes(this.currentBallNumber) && b.category === 'straight',
    );
    this.d.topbar.showResultBall(this.currentBallNumber, def?.color ?? 'dark');

    if (this.currentWinLose > 0) {
      this.d.topbar.showToast(`WIN  +${fmt(this.currentWinLose)}`, 'win');
      this.d.topbar.setStatus(`You win ${fmt(this.currentWinLose)}`);
    } else if (this.currentWinLose < 0) {
      this.d.topbar.showToast(`LOSE  ${fmt(this.currentWinLose)}`, 'lose');
      this.d.topbar.setStatus(`You lose ${fmt(-this.currentWinLose)}`);
    } else if (!this.d.state.bets.isEmpty) {
      this.d.topbar.showToast(`BALL ${this.currentBallNumber}`, 'info');
      this.d.topbar.setStatus('No win');
    } else {
      this.d.topbar.showToast(`BALL ${this.currentBallNumber}`, 'info');
      this.d.topbar.setStatus('Round ended');
    }

    window.setTimeout(() => this.d.topbar.hideToast(), RESULT_HOLD_MS);
  }

  // ============================================================
  // Countdown tick (server-driven, client-rendered)
  // ============================================================

  private startCountdown(totalSec: number): void {
    this.stopTick();
    const adjusted = Math.max(0, totalSec - BET_COUNTDOWN_OFFSET_SEC);
    this.tickRemaining = adjusted;
    this.d.topbar.showCountdown();
    this.d.topbar.setCountdown(adjusted, 'betting');
    this.d.state.setCountdown(adjusted);
    if (adjusted <= 0) return;
    this.tickTimer = window.setInterval(() => {
      this.tickRemaining -= 1;
      this.d.topbar.setCountdown(this.tickRemaining, 'betting');
      this.d.state.setCountdown(this.tickRemaining);
      if (this.tickRemaining <= 0) this.stopTick();
    }, 1000);
  }

  private stopTick(): void {
    if (this.tickTimer !== null) {
      window.clearInterval(this.tickTimer);
      this.tickTimer = null;
    }
  }

  // ============================================================
  // State → UI sync
  // ============================================================

  private onStateChange(next: GameStateSnapshot, prev: GameStateSnapshot): void {
    const nextDisp = displayedBalance(next);
    const prevDisp = displayedBalance(prev);
    if (nextDisp !== prevDisp) this.d.topbar.setBalance(nextDisp);
    if (next.bets !== prev.bets) {
      this.d.topbar.setBetTotal(next.bets.total);
      const allKeys = new Set([...prev.bets.keys, ...next.bets.keys]);
      for (const k of allKeys) this.d.panel.setStake(k, next.bets.amountFor(k));
    }
    if (next.selectedChipIndex !== prev.selectedChipIndex) {
      this.d.panel.setSelectedChip(next.selectedChipIndex);
    }
    if (next.config !== prev.config) {
      this.d.panel.bindChips(next.config);
      this.d.panel.setSelectedChip(next.selectedChipIndex);
    }
  }
}

// ====================================================================
// Helpers
// ====================================================================

function fmt(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

function ensureUrlEncoded(token: string): string {
  if (!token) return token;
  if (token.includes('%')) return token;
  return encodeURIComponent(token);
}

function parseGameUrl(currentGameUrl: string, loginToken: string): { gameToken: string; gsid: string } {
  if (!currentGameUrl) return { gameToken: loginToken, gsid: '' };
  try {
    const u = new URL(currentGameUrl, 'https://placeholder.invalid');
    const tk = u.searchParams.get('token');
    const gs = u.searchParams.get('gsid');
    return { gameToken: tk && tk.length > 0 ? tk : loginToken, gsid: gs ?? '' };
  } catch {
    return { gameToken: loginToken, gsid: '' };
  }
}

function num(v: unknown, dflt = 0): number {
  if (typeof v === 'number') return v;
  if (typeof v === 'string' && v !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : dflt;
  }
  return dflt;
}

function asMap(v: unknown): Record<string, unknown> | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  return v as Record<string, unknown>;
}

/** Server may send Chips as a CSV string ("50,100,…") OR as an array. */
function parseChipList(raw: unknown): number[] {
  if (Array.isArray(raw)) {
    return raw.map((n) => num(n)).filter((n) => n > 0);
  }
  if (typeof raw === 'string') {
    return raw.split(',').map((s) => num(s.trim())).filter((n) => n > 0);
  }
  return [];
}

/**
 * Find this player's record in a `Data` field that's an array of
 * `{UserName, …}` objects. Falls back to checking a single map.
 */
function findUserLine(data: unknown, userName: string): Record<string, unknown> | null {
  if (!userName) return null;
  const target = userName.toLowerCase();
  if (Array.isArray(data)) {
    for (const item of data) {
      const m = asMap(item);
      if (!m) continue;
      const u = String(m.UserName ?? m.userName ?? m.Username ?? m.username ?? '').toLowerCase();
      if (u && u === target) return m;
    }
    return null;
  }
  const m = asMap(data);
  if (!m) return null;
  const u = String(m.UserName ?? m.userName ?? m.Username ?? m.username ?? '').toLowerCase();
  return u && u === target ? m : null;
}

/** Server Result string: `"<ball>,@<winId>,…"` — matches REST. */
function parseResultString(s: string): { ballNumber: number; winningBetIds: number[] } {
  const winningBetIds: number[] = [];
  let ballNumber = 0;
  if (!s) return { ballNumber, winningBetIds };
  const parts = s.split(',').map((p) => p.trim()).filter(Boolean);
  for (const p of parts) {
    if (p.startsWith('@')) {
      const id = Number(p.slice(1));
      if (Number.isFinite(id)) winningBetIds.push(id);
    } else if (ballNumber === 0) {
      const n = Number(p);
      if (Number.isFinite(n)) ballNumber = n;
    }
  }
  if (ballNumber === 0) {
    const m = s.match(/\d+/);
    if (m) ballNumber = Number(m[0]);
  }
  return { ballNumber, winningBetIds };
}
